import { addDays } from 'date-fns';
import { endOfDay } from 'date-fns';
import { addMonths } from 'date-fns';
import { isValid } from 'date-fns';
import { startOfDay } from 'date-fns';
import { startOfMonth } from 'date-fns';
import { subDays } from 'date-fns';
import { subMonths } from 'date-fns';
import { z } from 'zod';

export const DateRange = z.object({
  start: z.date(),
  end: z.date(),
});

export type DateRange = z.infer<typeof DateRange>;

export const PartialDateRange = z.object({
  start: z.date().nullable(),
  end: z.date().nullable(),
});

export type PartialDateRange = z.infer<typeof PartialDateRange>;

export const Period = z.union([
  z.literal('1w'),
  z.literal('28d'),
  z.literal('3m'),
  z.literal('6m'),
  z.literal('1y'),
  z.literal('2y'),
  z.literal('3y'),
  z.literal('all'),
  z.literal('custom'),
]);

export type Period = z.infer<typeof Period>;

/**
 * Gets the date range for a given period.
 *
 * If you pass an anchor date, the range will be constructed relative to that anchor.
 * - If `anchorPosition === 'center'`, the anchor will be in the middle of the range (current behavior).
 * - If `anchorPosition === 'start'`, the anchor will be the very start of the range.
 * - If `anchorPosition === 'end'`, the anchor will be the very end of the range (default).
 *
 * If no valid anchor is provided, the current date is used.
 */
// eslint-disable-next-line complexity
export const getDateRangeForPeriod = ({
  period,
  anchor,
  anchorPosition = 'end',
}: {
  period: Period;
  anchor?: Date;
  anchorPosition?: 'start' | 'end' | 'center';
}): DateRange => {
  const now = new Date();
  const anchorValidated = anchor && isValid(anchor) ? anchor : now;

  // symmetric "N days" around the anchor with the anchor day in the middle
  const daysCentered = (totalDays: number) => {
    const left = Math.floor((totalDays - 1) / 2);
    const right = Math.ceil((totalDays - 1) / 2);
    const start = startOfDay(subDays(anchorValidated, left));
    const end = startOfDay(addDays(anchorValidated, right));
    return { start, end };
  };

  // non-symmetric, anchored at start
  const daysFromStart = (totalDays: number) => {
    const start = startOfDay(anchorValidated);
    const end = startOfDay(addDays(start, totalDays - 1));
    return { start, end };
  };

  // non-symmetric, anchored at end
  const daysToEnd = (totalDays: number) => {
    const end = startOfDay(anchorValidated);
    const start = startOfDay(subDays(end, totalDays - 1));
    return { start, end };
  };

  // symmetric month window using month boundaries (used for '3m')
  const monthsCenteredByBoundary = (monthsTotal: number) => {
    const half = monthsTotal / 2;
    const left = Math.floor(half);
    const right = Math.floor(half);
    const start = startOfMonth(subMonths(anchorValidated, left));
    const end = startOfMonth(addMonths(anchorValidated, right));
    return { start, end };
  };

  // boundary months, anchored at start (e.g. startOfMonth(anchor) → +N months)
  const monthsFromStartByBoundary = (monthsTotal: number) => {
    const start = startOfMonth(anchorValidated);
    const end = startOfMonth(addMonths(anchorValidated, monthsTotal));
    return { start, end };
  };

  // boundary months, anchored at end
  const monthsToEndByBoundary = (monthsTotal: number) => {
    const end = startOfMonth(anchorValidated);
    const start = startOfMonth(subMonths(anchorValidated, monthsTotal));
    return { start, end };
  };

  // day-centered months on each side of anchor (used for 6m, 1y, 2y, 3y)
  const monthsDayCentered = (monthsEachSide: number) => {
    const start = startOfDay(subMonths(anchorValidated, monthsEachSide));
    const end = startOfDay(addMonths(anchorValidated, monthsEachSide));
    return { start, end };
  };

  // start-anchored version of the above: total months = monthsEachSide * 2
  const monthsDayFromStart = (monthsEachSide: number) => {
    const start = startOfDay(anchorValidated);
    const end = startOfDay(addMonths(anchorValidated, monthsEachSide * 2));
    return { start, end };
  };

  // end-anchored version of the above
  const monthsDayToEnd = (monthsEachSide: number) => {
    const end = startOfDay(anchorValidated);
    const start = startOfDay(subMonths(anchorValidated, monthsEachSide * 2));
    return { start, end };
  };

  switch (period) {
    case '1w': {
      // 7 days
      if (anchorPosition === 'start') return daysFromStart(7);
      if (anchorPosition === 'end') return daysToEnd(7);
      return daysCentered(7);
    }

    case '28d': {
      if (anchorPosition === 'start') return daysFromStart(28);
      if (anchorPosition === 'end') return daysToEnd(28);
      return daysCentered(28);
    }

    case '3m': {
      // existing behavior: month before → anchor month → month after
      // that's a "3-ish month" window using boundaries
      if (anchorPosition === 'start') {
        // start at anchor month, go forward 2 months (total window similar to original)
        return monthsFromStartByBoundary(2);
      }
      if (anchorPosition === 'end') {
        // end at anchor month, go backward 2 months
        return monthsToEndByBoundary(2);
      }
      return monthsCenteredByBoundary(2);
    }

    case '6m': {
      // ±3 months from anchor day
      if (anchorPosition === 'start') return monthsDayFromStart(3);
      if (anchorPosition === 'end') return monthsDayToEnd(3);
      return monthsDayCentered(3);
    }

    case '1y': {
      // ±6 months from anchor day
      if (anchorPosition === 'start') return monthsDayFromStart(6);
      if (anchorPosition === 'end') return monthsDayToEnd(6);
      return monthsDayCentered(6);
    }

    case '2y': {
      // ±12 months from anchor day
      if (anchorPosition === 'start') return monthsDayFromStart(12);
      if (anchorPosition === 'end') return monthsDayToEnd(12);
      return monthsDayCentered(12);
    }

    case '3y': {
      // ±18 months from anchor day
      if (anchorPosition === 'start') return monthsDayFromStart(18);
      if (anchorPosition === 'end') return monthsDayToEnd(18);
      return monthsDayCentered(18);
    }

    case 'custom': {
      if (anchorPosition === 'start') return monthsDayFromStart(6);
      if (anchorPosition === 'end') return monthsDayToEnd(6);
      return monthsDayCentered(6);
    }
    case 'all':
    default: {
      const start = new Date('1900-01-01T00:00:00Z');
      const end = now;
      return { start, end };
    }
  }
};

export const lastMonthDateRange = (): DateRange => {
  const today = new Date();
  const start = startOfDay(subMonths(today, 1));
  const end = endOfDay(today);

  return { start, end };
};
